import { useContext, useEffect, useRef, useState } from 'react';
import Helmet from "react-helmet";
import api from "../../api/Base";
import { Link, useNavigate } from "react-router-dom";
import { InstanceContext } from "../../context/InstanceContext";

// Components
import Breadcrumb from "../../container/breadcrumb";
import ConditionalRender from "../../helpers/ConditionalRender";
import { Dropdown } from "../../components/forms";
import ResultsTable from "./ResultsTable";

// Types
import InstanceTarget from "../../types/instance/InstanceTarget";

// Style
import './style.scss'


interface SqlSearchProps {
    instances: InstanceTarget[],
}

const SQLSearch = (props: SqlSearchProps) => {
    const [loading, setLoading] = useState<boolean>(false)
    const [value, setValue] = useState<string>('')
    const [error, setError] = useState<string>('')
    const [results, setResults] = useState<any>([])
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [instanceId, setInstanceId] = useState<number>(NaN);
    const searchButtonRef = useRef<HTMLButtonElement | null>(null);

    const { instances } = useContext(InstanceContext);
    const navigate = useNavigate();

    const searchStatement = async (searchString: string, selectedInstance: any) => {
        setResults([])
        setLoading(true)

        const encodedSqlStatement = encodeURIComponent(searchString);
        navigate(`/sql-search?sql=${encodedSqlStatement}&instance=${selectedInstance}`)

        try {
            const instanceParam = Number(selectedInstance) ? `&id=${selectedInstance}` : ''
            const response = await api.post(`sql/search?sort=similarity+desc,id,sqlhash&limit=50${instanceParam}`, searchString);
            if (response?.data) {
                const filteredResults = response?.data.filter((row: any) => (
                    !row.sqltext.toLowerCase().startsWith('explain')
                ))

                setResults(filteredResults)
                setLoading(false)
                setError('');
            }
        } catch (err) {
            setError('Failed to read  data, request failed');
            setLoading(false)
        }
    }

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const sqlStatement = searchParams.get('sql');
        const selectedInstance = searchParams.get('instance');

        if (sqlStatement) {
            const handleParamValue = async () => {
                // URLSearchParams.get() already decodes the value, so no need for decodeURIComponent
                setValue(sqlStatement)
                await searchStatement(sqlStatement, selectedInstance)
            };
            void handleParamValue();
        }

        if (selectedInstance) {
            setInstanceId(Number(selectedInstance))
        }

        // Focus on the textarea after clearing data
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    }, []);

    const refresh = async () => {
        await searchStatement(value, instanceId)
    }

    const clearData = () => {
        setInstanceId(NaN)
        setValue('');

        // Focus on the textarea after clearing data
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    }

    const handleKeyPress = (event: any) => {
        if (event.key === 'Enter') {
            event.preventDefault(); // Prevent the default Enter key behavior (new line in textarea)
            if (!loading) {
                void searchStatement(value, instanceId)
            }
        }
    };

    return (
        <div id="content">
            <Helmet>
                <title>SQL Search - DBmarlin</title>
                <meta name="description" content="Search SQL" />
            </Helmet>

            {/* Heading */}
            <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                <Breadcrumb heading='SQL Search' instanceId={instanceId}>
                    <Link to="/sql-search">Analysis</Link>
                    <span className={"current-breadcrumb-link"}>SQL Search</span>
                </Breadcrumb>

            </div>

            <div className="search-area">

                <div className='search-options'>

                    <span onClick={() => clearData()}>Clear</span></div>
                <textarea ref={textareaRef} disabled={loading} id="searchField" onKeyPress={handleKeyPress}
                    placeholder="SQL Statement text to search for..." onChange={e => setValue(e.target.value)}
                    value={value} /><br />

                <div className='search-options'>
                    <div id="top-header-wrapper">
                        <div className="sql-search-instance">
                            <Dropdown
                                value={isNaN(instanceId) ? '' : instanceId}
                                onChange={(value) => setInstanceId(value === '' || value === null ? NaN : Number(value))}
                                options={[
                                    { value: '', label: 'All Instances' },
                                    ...[...props.instances].sort((a, b) => a.name.localeCompare(b.name)).map(item => ({ value: item.id, label: item.name }))
                                ]}
                                placeholder="Select Instance (Optional)"
                                showClear
                                filter
                                filterPlaceholder="Search instances"
                                ariaLabel="Instance"
                            />
                        </div>
                    </div>
                </div>
                <button disabled={loading || !value} onClick={() => searchStatement(value, instanceId)}
                    ref={searchButtonRef}
                    className='search-button'>Search for Statement <i className="fal fa-search fa-fw"
                        aria-hidden="true" /></button>
            </div>

            {/* Results Table */}
            <div className="row row-cols-1">
                <div className="col">
                    <div className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-server fa-fw" aria-hidden="true" />
                            Matching Statements
                            <span className="badge bg-info"
                                data-tooltip-id="global-tooltip" data-tooltip-content="Total matching atatements">{results.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                data-bs-target="#collapseResults" aria-expanded="false"
                                aria-controls="collapseResults" />
                        </div>
                        <div id="collapseResults" className="card-body collapse show">
                            <ConditionalRender if={loading}>
                                <div className="w-100 text-center text-muted my-4">
                                    <div className="loader spinner chartSpinner">
                                    </div>
                                    <p className="my-3">
                                        Loading data...
                                    </p>
                                </div>
                            </ConditionalRender>
                            <ConditionalRender if={!results.length && !loading && instances.length}>
                                <div id="collapseStatements" className="card-body">
                                    <div className="w-100 text-center text-muted my-3">
                                        <i className="fal fa-ban fa-fw fa-2x" />
                                        {error ? <p className='request-error'>
                                            {`${error}`}. <span onClick={() => refresh()}>Try again</span>
                                        </p> : <p>No Matching Statements</p>}
                                    </div>
                                </div>
                            </ConditionalRender>
                            <ConditionalRender if={results.length && !loading}>
                                <ResultsTable loading={loading} list={results} />
                            </ConditionalRender>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SQLSearch;
